import type { PortalEvent } from "@/lib/portal-socket-engine";

/**
 * The ringing banner's state (doorbell live calls spec, "Portal socket"):
 * `call.incoming {call_id, asking_for, location, expires_at, is_test}` adds a call,
 * `call.ended {call_id, …}` removes it (in every tab), and the banner closes
 * itself at `expires_at`. That moment is the server's; a member's clock may be
 * off by minutes, so the deadline is clamped to 20–90 s from when the push
 * arrived (ring times are 20–90 s) — `call.ended` still closes it on time.
 */

export const CALL_INCOMING_EVENT = "call.incoming";
export const CALL_ENDED_EVENT = "call.ended";
export const MIN_RING_MS = 20_000;
export const MAX_RING_MS = 90_000;

export interface IncomingCall {
  callId: number;
  askingFor: string;
  /** Where the visitor is, e.g. "Main entrance, L26"; "" when unknown (show "reception"). */
  location: string;
  isTest: boolean;
  /** Local time (ms) the banner closes itself. */
  deadline: number;
}

export function ringDeadline(expiresAt: unknown, receivedAt: number): number {
  const at = typeof expiresAt === "string" ? Date.parse(expiresAt) : NaN;
  const left = Number.isFinite(at) ? at - receivedAt : MAX_RING_MS;
  return receivedAt + Math.min(MAX_RING_MS, Math.max(MIN_RING_MS, left));
}

export function callIdOf(e: PortalEvent): number | null {
  const id = Number(e.call_id);
  return Number.isInteger(id) && id > 0 ? id : null;
}

export function parseIncoming(e: PortalEvent, receivedAt: number): IncomingCall | null {
  const callId = callIdOf(e);
  if (callId === null) return null;
  return {
    callId,
    askingFor: typeof e.asking_for === "string" ? e.asking_for : "",
    location: typeof e.location === "string" ? e.location : "",
    isTest: e.is_test === true,
    deadline: ringDeadline(e.expires_at, receivedAt),
  };
}

export function addIncoming(list: readonly IncomingCall[], call: IncomingCall): IncomingCall[] {
  return [...list.filter((c) => c.callId !== call.callId), call];
}

export function removeIncoming(list: readonly IncomingCall[], callId: number): IncomingCall[] {
  return list.filter((c) => c.callId !== callId);
}

/** The call to show: the first still ringing that isn't the call page on screen. */
export function activeIncoming(list: readonly IncomingCall[], now: number, hiddenCallId: number | null): IncomingCall | null {
  return list.find((c) => c.deadline > now && c.callId !== hiddenCallId) ?? null;
}

export function callIdFromPath(pathname: string): number | null {
  const match = /^\/doorbell\/call\/(\d+)\/?$/.exec(pathname);
  return match ? Number(match[1]) : null;
}
